import React from 'react';
import {
  accessTokenState,
  amountState,
  emailState,
  loginOtpVerifyCount,
  loginOtpVerifyDate,
  merchantAddressState,
  merchantNameState,
  nameState,
  phoneState,
  themePreferenceState,
} from '../atoms';

const isBrowser = typeof window !== 'undefined';

const storageAccessors = {
  session: {
    getItem: (key) => {
      if (!isBrowser) return null;
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      if (!isBrowser) return;
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        // ignore quota/security errors
      }
    },
    removeItem: (key) => {
      if (!isBrowser) return;
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
  },
  local: {
    getItem: (key) => {
      if (!isBrowser) return null;
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      if (!isBrowser) return;
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // ignore
      }
    },
    removeItem: (key) => {
      if (!isBrowser) return;
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
  },
};

const DEFAULT_STORAGES = Object.freeze(['session']);

const parseStoredValue = (raw, descriptor) => {
  if (raw === null || raw === 'undefined') {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw);
    return descriptor.deserialize ? descriptor.deserialize(parsed) : parsed;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error parsing stored value for key "${descriptor.key}"`, error);
    }
    return undefined;
  }
};

const readInitialValue = (descriptor) => {
  const storageOrder = descriptor.storages || DEFAULT_STORAGES;
  for (const storageType of storageOrder) {
    const storage = storageAccessors[storageType];
    if (!storage) {
      continue;
    }
    const raw = storage.getItem(descriptor.key);
    const parsed = parseStoredValue(raw, descriptor);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return descriptor.defaultValue;
};

const usePersistentState = (descriptor) => {
  const storageOrder = descriptor.storages || DEFAULT_STORAGES;
  const [state, setState] = React.useState(() => readInitialValue(descriptor));

  const persistValue = React.useCallback(
    (value) => {
      for (const storageType of storageOrder) {
        const storage = storageAccessors[storageType];
        if (!storage) {
          continue;
        }
        if (typeof value === 'undefined') {
          storage.removeItem(descriptor.key);
        } else {
          try {
            storage.setItem(descriptor.key, JSON.stringify(value));
          } catch {
            // ignore
          }
        }
      }
    },
    [descriptor.key, storageOrder],
  );

  React.useEffect(() => {
    persistValue(state);
  }, [persistValue, state]);

  const setSyncedState = React.useCallback(
    (valueOrUpdater) => {
      setState((prev) => {
        const resolvedValue = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
        return resolvedValue;
      });
    },
    [],
  );

  return React.useMemo(
    () => ({
      state,
      setState: setSyncedState,
    }),
    [setSyncedState, state],
  );
};

export const GlobalStateContext = React.createContext(null);

export const GlobalStateProvider = ({ children }) => {
  const merchantAddress = usePersistentState(merchantAddressState);
  const merchantName = usePersistentState(merchantNameState);
  const displayName = usePersistentState(nameState);
  const phone = usePersistentState(phoneState);
  const email = usePersistentState(emailState);
  const amount = usePersistentState(amountState);
  const accessToken = usePersistentState(accessTokenState);
  const otpAttemptCount = usePersistentState(loginOtpVerifyCount);
  const otpAttemptDate = usePersistentState(loginOtpVerifyDate);
  const themePreference = usePersistentState(themePreferenceState);

  const resolvedTheme = themePreference.state === 'dark' ? 'dark' : 'light';

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const contextValue = React.useMemo(
    () => ({
      [merchantAddressState.key]: merchantAddress,
      [merchantNameState.key]: merchantName,
      [nameState.key]: displayName,
      [phoneState.key]: phone,
      [emailState.key]: email,
      [amountState.key]: amount,
      [accessTokenState.key]: accessToken,
      [loginOtpVerifyCount.key]: otpAttemptCount,
      [loginOtpVerifyDate.key]: otpAttemptDate,
      [themePreferenceState.key]: themePreference,
    }),
    [
      merchantAddress,
      merchantName,
      displayName,
      phone,
      email,
      amount,
      accessToken,
      otpAttemptCount,
      otpAttemptDate,
      themePreference,
    ],
  );

  return <GlobalStateContext.Provider value={contextValue}>{children}</GlobalStateContext.Provider>;
};
